import { Card, Tooltip } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import i18next from 'i18next';
import { DataGrid, enUS, frFR, GridCellParams, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { AlertSeverity } from '../../../utils/enum/Alerts';
import { AuthHeader } from '../../../utils/helpers/auth-headers';
import './Alarms.scss';
import { setAlertData } from '../../../stores/alertStore';
import { Alarm } from '../../../models/Alarm';
import { PageRowsOptions } from '../../../models/DataGridConf';
import { RootState } from '../../../stores/globalStore';
import { setConnectedUser } from '../../../stores/authenticationStore';

function InstanceAlarms() {
  const { t } = useTranslation();

  const connectedUser = useSelector((state: RootState) => state.authentication.connectedUser);

  const i18nLang = i18next.language;
  const [gridLang, setGridLang] = useState(frFR);
  const paramsRoute = useParams();
  const dispatch = useDispatch();
  const [alarms, setAlarms] = useState([]);
  const [pageSize, setPageSize] = useState<number>(connectedUser.prefs?.nbRow ? connectedUser.prefs.nbRow : 25);
  const pageRowsOptions = PageRowsOptions;
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/alarms`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setAlarms(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  }, [paramsRoute]);

  useEffect(() => {
    if (i18nLang === 'en') {
      setGridLang(enUS);
    } else {
      setGridLang(frFR);
    }
  }, [i18nLang]);

  const navigateToElement = async (rowData: Alarm) => {
    const rWorkers = await axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/workers`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} });
    const rManagementNodes = await axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/mgtNodes`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} });
    const workers = rWorkers.data.data;
    const managementNodes = rManagementNodes.data.data;
    for (const worker of workers) {
      if (rowData.node === worker.address) {
        navigate(`../configuration/${worker.id}`);
      }
    }
    for (const managementNode of managementNodes) {
      if (rowData.node === managementNode.address) {
        navigate('../configuration');
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: '_id', headerName: 'ID', minWidth: 90, flex: 1, hide: true
    },
    {
      field: 'name',
      renderHeader: () => (
        <Tooltip title={t('InstanceAlarms.table.name') as string} arrow>
          <span className="strong-header">{t('InstanceAlarms.table.name')}</span>
        </Tooltip>
      ),
      description: 'name of alarm',
      minWidth: 150,
      maxWidth: 250,
      flex: 1
    },
    {
      field: 'node',
      renderHeader: () => (
        <Tooltip title={t('InstanceAlarms.table.node') as string} arrow>
          <span className="strong-header">{t('InstanceAlarms.table.node')}</span>
        </Tooltip>
      ),
      maxWidth: 150,
      minWidth: 100,
      flex: 1
    },
    {
      field: 'instance',
      renderHeader: () => (
        <Tooltip title={t('InstanceAlarms.table.instance') as string} arrow>
          <span className="strong-header">{t('InstanceAlarms.table.instance')}</span>
        </Tooltip>
      ),
      minWidth: 400,
      flex: 1
    },
    {
      field: 'level',
      renderHeader: () => (
        <Tooltip title={t('InstanceAlarms.table.level') as string} arrow>
          <span className="strong-header">{t('InstanceAlarms.table.level')}</span>
        </Tooltip>
      ),
      maxWidth: 110,
      minWidth: 110,
      flex: 1
    },
    {
      field: 'details',
      renderHeader: () => (
        <Tooltip title={t('InstanceAlarms.table.details') as string} arrow>
          <span className="strong-header">{t('InstanceAlarms.table.details')}</span>
        </Tooltip>
      ),
      minWidth: 110,
      flex: 1
    },
    {
      field: 'identifier',
      renderHeader: () => (
        <Tooltip title={t('InstanceAlarms.table.identifier') as string} arrow>
          <span className="strong-header">{t('InstanceAlarms.table.identifier')}</span>
        </Tooltip>
      ),
      minWidth: 90,
      maxWidth: 100,
      flex: 1
    },
    {
      field: 'timeRaised',
      renderHeader: () => (
        <Tooltip title={t('InstanceAlarms.table.timeRaised') as string} arrow>
          <span className="strong-header">{t('InstanceAlarms.table.timeRaised')}</span>
        </Tooltip>
      ),
      minWidth: 110,
      maxWidth: 150,
      flex: 1,
      renderCell: (params) => new Date(params.row.timeRaised).toLocaleString(i18nLang)
    },
    {
      field: 'resourceUri',
      renderHeader: () => (
        <Tooltip title={t('InstanceAlarms.table.resourceUri') as string} arrow>
          <span className="strong-header">{t('InstanceAlarms.table.resourceUri')}</span>
        </Tooltip>
      ),
      maxWidth: 260,
      minWidth: 110,
      flex: 1
    }
  ];

  const updateNbRowUser = (updateNbRow: number) => {  
    const updatePrefs = { ...connectedUser.prefs };
    updatePrefs.nbRow = updateNbRow;
    const modifyPrefs = {
      prefs: updatePrefs
    };
    axios.post(`${window._env_.REACT_APP_API_URI}/user/prefs`, modifyPrefs, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        dispatch(setConnectedUser({ connectedUser: response.data.data }));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const changePageSize = (newPage: number) => {
    setPageSize(newPage);
    updateNbRowUser(newPage);
  };

  return (
    <div className="category-container">
      <Card>
        <div>
          <DataGrid
            getRowId={(r) => r._id}
            rows={alarms}
            columns={columns}
            rowsPerPageOptions={pageRowsOptions}
            pageSize={pageSize}
            onPageSizeChange={(newPage) => changePageSize(newPage)}
            components={{ Toolbar: GridToolbar }}
            onCellClick={(params: GridCellParams) => {
              navigateToElement(params.row);
            }}
            getRowClassName={() => 'pointer'}
            pagination
            autoHeight
            disableSelectionOnClick
            localeText={gridLang.components.MuiDataGrid.defaultProps.localeText}
            density="compact"
          />
        </div>
      </Card>
    </div>
  );
}

export default InstanceAlarms;
